/**
 * Invisible reCAPTCHA v3.
 *
 * The visitor never sees a challenge. Google scores the session in the
 * background and the backend checks that score before acting.
 *
 * The script is loaded on first use rather than on every page. It is ~130KB
 * from a third-party origin, and loading it eagerly would put a Google
 * connection on the critical path of a site whose whole argument is that you
 * should own your own infrastructure — for a form most visitors never touch.
 */

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

const SITE_KEY = process.env.GATSBY_RECAPTCHA_SITE_KEY ?? "";

let loading: Promise<void> | null = null;

const load = (): Promise<void> => {
  if (typeof window === "undefined" || !SITE_KEY) return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();
  // Cached, so several forms on one page share a single script tag.
  if (loading) return loading;

  loading = new Promise<void>((resolve) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    script.async = true;
    script.onload = () => resolve();
    // Resolve on error too: a blocked or failed script must not stop the form
    // from submitting. The backend treats a missing token as unverified and
    // falls back to its own rate limits.
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
  return loading;
};

/**
 * A token for one action, or null when reCAPTCHA is unavailable.
 *
 * Never throws and never blocks: an ad blocker, an outage, or no site key at
 * all all return null, and the caller submits without a token.
 */
export const getRecaptchaToken = async (
  action: string
): Promise<string | null> => {
  if (typeof window === "undefined" || !SITE_KEY) return null;

  try {
    await load();
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) return null;

    return await new Promise<string | null>((resolve) => {
      // Tokens expire after two minutes, so this must run at submit time
      // rather than on mount — a visitor who reads the page before typing
      // would otherwise send a token Google has already discarded.
      grecaptcha.ready(() => {
        grecaptcha
          .execute(SITE_KEY, { action })
          .then(resolve)
          .catch(() => resolve(null));
      });
    });
  } catch {
    return null;
  }
};
